import { useCallback, useEffect, useState } from "react";
import api, { formatApiError, downloadBlob } from "../lib/api";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Download, Ticket } from "@phosphor-icons/react";
import ClaroTicketQuickEdit from "./ClaroTicketQuickEdit";

const VIEWS = [["open", "Open"], ["closed", "Closed"], ["cancelled", "Cancelled"], ["all", "All"]];
const SOURCES = [["all", "All sources"], ["security_cve", "Security CVE"], ["app_baseline", "App baseline / drift"],
  ["security_drift", "Config drift"], ["soc2_recurring", "SOC 2 recurring"]];

export default function ConsoleClaroTickets() {
  const [view, setView] = useState("open");
  const [source, setSource] = useState("all");
  const [data, setData] = useState(null);
  const [editId, setEditId] = useState(null);
  const label = Object.fromEntries(data?.statuses || []);

  const load = useCallback(() => {
    api.get("/console/claro-tickets", { params: { view, source: source === "all" ? undefined : source } })
      .then(({ data: d }) => setData(d)).catch((e) => toast.error(formatApiError(e.response?.data?.detail)));
  }, [view, source]);
  useEffect(() => { load(); }, [load]);

  const cancel = async (t) => {
    const reason = window.prompt(`Reason for cancelling "${t.title}" (required):`, "");
    if (reason === null) return;
    try { await api.post(`/console/claro-tickets/${t.id}/cancel`, { reason }); toast.success("Ticket cancelled"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const exportXlsx = () => downloadBlob(
    `/console/claro-tickets/export.xlsx?view=${view}${source !== "all" ? `&source=${source}` : ""}`, `claro_tickets_${view}.xlsx`);

  return (
    <section className="space-y-4" data-testid="claro-tickets-panel">
      <div>
        <h3 className="font-semibold flex items-center gap-2"><Ticket size={18} /> ClaroCompliance Tickets</h3>
        <p className="text-sm text-muted-foreground mt-1 max-w-3xl">
          Claro's own platform tickets only (security CVEs, app-baseline/drift, SOC 2 recurring) from the private
          ClaroCompliance workspace. Tenant tickets are never shown here.
        </p>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        {VIEWS.map(([k, l]) => (
          <button key={k} onClick={() => setView(k)} data-testid={`claro-tickets-view-${k}`}
            className={`border px-3 py-1.5 text-xs font-semibold rounded-none ${view === k ? "bg-primary text-primary-foreground border-primary" : "border-border hover:bg-secondary"}`}>
            {l} ({data?.counts?.[k] ?? "…"})
          </button>
        ))}
        <Select value={source} onValueChange={setSource}>
          <SelectTrigger className="rounded-none w-52 h-8 text-xs" data-testid="claro-tickets-source"><SelectValue /></SelectTrigger>
          <SelectContent>{SOURCES.map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
        </Select>
        <Button size="sm" variant="outline" className="rounded-none ml-auto" onClick={exportXlsx} data-testid="claro-tickets-export"><Download size={14} className="mr-1" />Export Excel</Button>
      </div>
      <div className="border border-border overflow-x-auto">
        <table className="w-full text-xs min-w-[900px]">
          <thead className="bg-muted/60 text-left">
            <tr>{["Title", "Source", "Status", "Priority", "Assignee", "Due", "Created", ""].map((h) => <th key={h} className="px-3 py-2 font-semibold">{h}</th>)}</tr>
          </thead>
          <tbody>
            {(data?.tickets || []).map((t) => (
              <tr key={t.id} className="border-t border-border align-top hover:bg-primary/5 cursor-pointer" data-testid={`claro-ticket-${t.id}`} onClick={() => setEditId(t.id)}>
                <td className="px-3 py-2 font-medium max-w-[380px]">{t.title}{t.cancel_reason && <div className="text-muted-foreground font-normal">Cancelled: {t.cancel_reason}</div>}</td>
                <td className="px-3 py-2 whitespace-nowrap">{t.source || "manual"}</td>
                <td className="px-3 py-2 whitespace-nowrap">{label[t.status] || t.status}</td>
                <td className="px-3 py-2">{t.priority}</td>
                <td className="px-3 py-2">{t.assignee}</td>
                <td className="px-3 py-2 whitespace-nowrap">{t.due_date || "—"}</td>
                <td className="px-3 py-2 whitespace-nowrap">{(t.created_at || "").slice(0, 10)}</td>
                <td className="px-3 py-2">
                  {!["closed", "completed", "spot_check_reviewed", "retired", "cancelled"].includes(t.status) && (
                    <button onClick={(e) => { e.stopPropagation(); cancel(t); }} className="text-red-600 hover:underline" data-testid={`claro-ticket-cancel-${t.id}`}>Cancel</button>
                  )}
                </td>
              </tr>
            ))}
            {data && !data.tickets.length && <tr><td colSpan={8} className="px-3 py-6 text-center text-muted-foreground">No tickets in this view.</td></tr>}
          </tbody>
        </table>
      </div>
      {data && data.total_shown > data.tickets.length && <p className="text-xs text-muted-foreground">Showing first {data.tickets.length} of {data.total_shown}. Export for the full list.</p>}
      <ClaroTicketQuickEdit ticketId={editId} onClose={() => setEditId(null)} onSaved={load} />
    </section>
  );
}
